/**
 * Reveal / highlight / diff context layer for the collab plan display.
 *
 * Cross-cutting, non-component plumbing shared by CollabPlanDisplay, its field
 * components (planFields.tsx), and the Test Instructions view: the field-diff /
 * reveal / highlight contexts, the hooks that read them, and small field helpers.
 * Kept in a .ts module (no components) so Fast Refresh boundaries stay clean.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { FieldDiff } from "../../../models/audit/collabFieldDiff";

/**
 * A request to reveal one diffed field: switch to its tab, expand the
 * collapsed sections containing it, scroll it into view, and flash it. The
 * nonce distinguishes repeat clicks on the same field.
 */
export interface PlanRevealTarget {
  key: string;
  nonce: number;
}

/** The latest revision's diffs, keyed by field key, for deep field components
 *  (the structure tab reads the map directly). */
export const PlanFieldDiffContext = createContext<Map<string, FieldDiff>>(
  new Map(),
);

/** The active reveal request, broadcast so collapsibles on the target field's
 *  path can open themselves and the field itself can scroll/flash. */
export const PlanRevealContext = createContext<PlanRevealTarget | null>(null);

/** The field key currently highlighted in the plan (a chat chip's target). Unlike a
 *  reveal, this persists: the amber ring stays until another field is revealed or a new
 *  edit/turn clears it. Kept separate from the one-shot scroll so a remount (tab switch,
 *  fold-out) re-highlights without re-scrolling. */
export const PlanHighlightContext = createContext<string | null>(null);

/**
 * Scroll-to and highlight behaviour for one diffed field. Attach the returned ref to the
 * field's outermost element; while `isHighlighted` is true, render the amber ring.
 *
 * The two are deliberately decoupled: the scroll is a one-shot per reveal (it waits a
 * frame so ancestors opened by the same reveal request render first), while the highlight
 * is a persistent selection driven by PlanHighlightContext — it stays until the user
 * reveals another field or starts a new edit/chat.
 */
export const useFieldReveal = (fieldKey: string | undefined) => {
  const revealTarget = useContext(PlanRevealContext);
  const highlightedKey = useContext(PlanHighlightContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fieldKey || !revealTarget || revealTarget.key !== fieldKey) return;
    const frame = requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [revealTarget, fieldKey]);

  const isHighlighted = !!fieldKey && highlightedKey === fieldKey;
  return { ref, isHighlighted };
};

export const FIELD_HIGHLIGHT_CLASS = "ring-2 ring-amber-400 bg-amber-100";

/**
 * The prior value to strike through above a field, or undefined for none.
 * A diff is only claimed while the field still holds the revision's new text,
 * so a manual edit (or a later revision) clears the strikethrough on its own.
 */
export const previousValueFor = (
  diff: FieldDiff | undefined,
  currentValue: string | null | undefined,
): string | undefined => {
  if (!diff || diff.before === "") return undefined;
  return (currentValue ?? "") === diff.after ? diff.before : undefined;
};

/**
 * A local text draft that commits on blur, without being clobbered mid-edit.
 *
 * The draft re-syncs to `value` whenever the plan changes — except while the field
 * is focused, where the user's keystrokes are the source of truth. Every plan patch
 * replaces the whole plan object, so a patch landing for a *different* field (e.g.
 * after blurring field A and typing in field B before A's round-trip returns) would
 * otherwise reset this field's draft and drop in-flight keystrokes.
 *
 * Wire `onFocus` onto the input and call `endEditing()` at the start of your blur
 * handler (before committing) so syncing resumes once the edit lands.
 */
export const useCommittableDraft = (value: string) => {
  const [draft, setDraft] = useState(value);
  const isFocusedRef = useRef(false);
  useEffect(() => {
    if (!isFocusedRef.current) setDraft(value);
  }, [value]);
  const onFocus = () => {
    isFocusedRef.current = true;
  };
  const endEditing = () => {
    isFocusedRef.current = false;
  };
  return { draft, setDraft, onFocus, endEditing };
};

/**
 * A pending removal for the confirm dialog. `blocked` requests render an
 * explanation with no destructive action, for removals the backend would
 * refuse (an attribute still referenced by a testing table).
 */
export type DeleteRequest =
  | {
      title: string;
      description: string;
      onConfirm: () => void;
      blocked?: false;
    }
  | { title: string; description: string; blocked: true };

/** Opens the confirm dialog for a pending removal (or a blocked explanation). */
export type RequestDelete = (opts: DeleteRequest) => void;

/**
 * Force-opens a disclosure when the active reveal request targets a field
 * inside it, declared by the key prefixes the disclosure's subtree contains.
 */
export const useRevealOpen = (
  revealPrefixes: string[] | undefined,
  setIsOpen: (open: boolean) => void,
) => {
  const revealTarget = useContext(PlanRevealContext);
  useEffect(() => {
    if (
      revealTarget &&
      revealPrefixes?.some((prefix) => revealTarget.key.startsWith(prefix))
    ) {
      setIsOpen(true);
    }
    // revealPrefixes is a fresh array literal each render; keying on the
    // target alone avoids re-opening a row the user closed since the reveal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealTarget]);
};

/** Shared card shell for attribute/testing-table cards (mockup styling). */
export const EDIT_CARD_CLASS =
  "group relative rounded-md border border-stone-200 bg-stone-50 transition-colors hover:border-stone-300 hover:bg-stone-100";
